import {
  Activity,
  AlertTriangle,
  ArrowRightLeft,
  BarChart3,
  Building2,
  Bus,
  ChevronDown,
  ClipboardList,
  HardHat,
  HelpCircle,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Puzzle,
  Scale,
  Shield,
  ShieldAlert,
  User,
  Users,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Link, NavLink, Outlet } from 'react-router-dom';

import { BrandMark } from '@/components/BrandMark';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { NotificationBell } from '@/components/NotificationBell';
import { useAuth } from '@/contexts/AuthContext';
import { usePmProperty } from '@/hooks/usePmProperty';
import { useRoleSwitcher } from '@/hooks/useRoleSwitcher';
import { getRoleLabel, isCommercial } from '@/lib/property-type';
import { getPropertyConfig } from '@/services/api.client';
import type { UserRole } from '@/types';

// ---------------------------------------------------------------------------
// Nav definition — each item carries the roles allowed to see it.
// security_supervisor cannot access Modules (PM-W-05), Property Setup (PM-W-06),
// or Compliance (PM-W-09) per SI-07.
// ---------------------------------------------------------------------------
const PM_ROLES: UserRole[] = ['property_manager', 'security_supervisor'];
const PM_ONLY: UserRole[] = ['property_manager'];

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  end?: boolean;
  allowedRoles: UserRole[];
  /**
   * Property-type gating for the nav item.
   *  - `'residential'`: shown on residential + mixed (and during type load).
   *  - `'commercial'`:  shown on commercial + mixed only (hidden during load).
   *  - omitted:         shown on every property type.
   *
   * The asymmetry is deliberate: residential-flavoured items are the historic
   * default so falling open on load keeps existing screens visible, while
   * commercial items are new and should not flash before the type confirms.
   */
  surfaces?: 'residential' | 'commercial';
}

const navSections: Array<{ label: string; items: NavItem[] }> = [
  {
    label: 'Operations',
    items: [
      {
        to: '/manager',
        label: 'Dashboard',
        icon: LayoutDashboard,
        end: true,
        allowedRoles: PM_ROLES,
      },
      {
        to: '/manager/access-log',
        label: 'Access Log',
        icon: ClipboardList,
        allowedRoles: PM_ROLES,
      },
      { to: '/manager/incidents', label: 'Incidents', icon: AlertTriangle, allowedRoles: PM_ROLES },
      {
        to: '/manager/crew-sessions',
        label: 'Contractor Crew',
        icon: HardHat,
        allowedRoles: PM_ROLES,
      },
      { to: '/manager/guards', label: 'Guards & Gates', icon: Shield, allowedRoles: PM_ROLES },
      {
        to: '/manager/trusted-operators',
        label: 'Trusted Operators',
        icon: Bus,
        allowedRoles: PM_ONLY,
      },
    ],
  },
  {
    label: 'Management',
    items: [
      { to: '/manager/users', label: 'Users', icon: Users, allowedRoles: PM_ROLES },
      {
        to: '/manager/companies',
        label: 'Companies',
        icon: Building2,
        allowedRoles: PM_ROLES,
        surfaces: 'commercial',
      },
      { to: '/manager/modules', label: 'Modules', icon: Puzzle, allowedRoles: PM_ONLY },
      { to: '/manager/property', label: 'Settings', icon: Building2, allowedRoles: PM_ONLY },
    ],
  },
  {
    label: 'Reporting',
    items: [
      { to: '/manager/compliance', label: 'Compliance', icon: Scale, allowedRoles: PM_ONLY },
      {
        to: '/manager/announcements',
        label: 'Announcements',
        icon: Megaphone,
        allowedRoles: PM_ROLES,
      },
      {
        to: '/manager/analytics',
        label: 'Analytics',
        icon: BarChart3,
        allowedRoles: PM_ROLES,
      },
    ],
  },
];

function initials(name: string) {
  return name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

// Nav link that closes the mobile off-canvas sidebar on tap, so the selected
// page is visible immediately instead of staying hidden behind the menu.
function PmNavLink({ item }: { item: NavItem }) {
  const { isMobile, setOpenMobile } = useSidebar();
  return (
    <SidebarMenuButton asChild>
      <NavLink
        to={item.to}
        end={item.end === true}
        onClick={() => {
          if (isMobile) setOpenMobile(false);
        }}
        className={({ isActive }) =>
          isActive ? 'text-sidebar-primary-foreground bg-sidebar-accent' : ''
        }
      >
        <item.icon className="size-4" />
        <span>{item.label}</span>
      </NavLink>
    </SidebarMenuButton>
  );
}

const ROLE_LABELS: Record<string, string> = {
  resident: 'Resident',
  property_manager: 'Manager',
  security_supervisor: 'Supervisor',
  enterprise_tenant_admin: 'Enterprise Admin',
  employee: 'Employee',
  guard: 'Guard',
  org_admin: 'Org Admin',
};

export function PmLayout() {
  const { user, signOut } = useAuth();

  const userRoles = user?.propertyRoles.map((r) => r.role) ?? [];
  const pm = usePmProperty();
  const propertyId = pm?.propertyId;

  const isPm = userRoles.includes('property_manager');
  const currentRole: UserRole = isPm ? 'property_manager' : 'security_supervisor';
  const { switchableRoles, switchTo, hasMultipleRoles } = useRoleSwitcher(currentRole);

  const propertyConfigQuery = useQuery({
    queryKey: ['pm', 'property-config', propertyId],
    queryFn: () => getPropertyConfig(propertyId ?? ''),
    enabled: !!propertyId,
  });
  const propertyType = propertyConfigQuery.data?.propertyType;
  const commercial = isCommercial(propertyType);

  const handleSignOut = () => {
    void signOut();
  };

  const primaryRole: UserRole = isPm ? 'property_manager' : 'security_supervisor';
  const roleLabel = getRoleLabel(primaryRole, propertyType);

  return (
    <SidebarProvider>
      <Sidebar variant="inset" collapsible="icon">
        {/* Brand header */}
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" asChild>
                <Link to="/manager">
                  <BrandMark className="size-8 shrink-0" aria-hidden />
                  <div className="flex flex-col leading-tight">
                    <span className="font-semibold tracking-tight text-sm">Secure Labs</span>
                    <span className="text-xs text-muted-foreground">{roleLabel}</span>
                  </div>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        {/* Main nav */}
        <SidebarContent>
          {navSections.map((section) => {
            const visibleItems = section.items.filter((item) => {
              const roleAllowed = item.allowedRoles.some((r) => userRoles.includes(r));
              if (!roleAllowed) return false;
              if (item.surfaces === 'residential' && commercial) return false;
              if (
                item.surfaces === 'commercial' &&
                propertyType !== undefined &&
                propertyType !== 'mixed' &&
                !commercial
              ) {
                return false;
              }
              return true;
            });
            if (!visibleItems.length) return null;
            return (
              <SidebarGroup key={section.label}>
                <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {visibleItems.map((item) => (
                      <SidebarMenuItem key={item.to}>
                        <PmNavLink item={item} />
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            );
          })}
        </SidebarContent>

        {/* User footer */}
        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => {
                  if (window.Intercom) {
                    window.Intercom('show');
                  } else {
                    window.open('mailto:support@securelabsystems.com', '_blank');
                  }
                }}
                className="text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <HelpCircle className="size-4" />
                <span>Help &amp; Support</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => window.open('https://status.securelabsystems.com', '_blank')}
                className="text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <Activity className="size-4" />
                <span>System Status</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton size="lg">
                    <Avatar className="size-8 shrink-0">
                      <AvatarFallback className="bg-primary/20 text-[var(--avatar-initials-fg)] text-xs font-semibold">
                        {user ? initials(user.fullName) : '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col items-start min-w-0 flex-1">
                      <span className="truncate text-sm font-medium">{user?.fullName}</span>
                      <span className="truncate text-xs text-muted-foreground">{user?.email}</span>
                    </div>
                    <ChevronDown className="size-4 ml-auto shrink-0" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="start" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col gap-1">
                      <p className="text-sm font-medium">{user?.fullName}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem asChild>
                      <Link to="/manager/account">
                        <User className="mr-2 size-4" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    {hasMultipleRoles &&
                      switchableRoles.map((sr) => (
                        <DropdownMenuItem
                          key={sr.role}
                          onClick={() => switchTo(sr)}
                          className="cursor-pointer"
                        >
                          <ArrowRightLeft className="mr-2 size-4" />
                          Switch to {ROLE_LABELS[sr.role] ?? sr.role}
                        </DropdownMenuItem>
                      ))}
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => void handleSignOut()}
                    className="text-destructive focus:text-destructive"
                  >
                    <LogOut className="mr-2 size-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      {/* Main content area */}
      <SidebarInset>
        {/* Topbar */}
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-6">
          <SidebarTrigger className="-ml-1" />
          <div className="flex-1" />
          <NotificationBell to="/manager/notifications" />
          <Button
            variant="outline"
            size="sm"
            asChild
            className="border-[var(--status-amber)]/50 text-[var(--status-amber-text)] hover:bg-[var(--status-amber)]/10 hover:border-[var(--status-amber)]"
          >
            <Link to="/manager/emergency">
              <ShieldAlert className="size-4 mr-1.5" />
              Emergency
            </Link>
          </Button>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
